import { useKeycloak } from "@react-keycloak/web";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { USER } from "@types/user";
import { LOGIN_STATES } from "@constants/Enums";
import { setHeaders } from "@services/StateManagement/ApiSlice";
import {
  setAuthState,
  setIsAdmin,
  setIsFirstUser,
} from "@services/StateManagement/AuthSlice";
import { setCompanyIdState } from "@services/StateManagement/CompanySlice";
import { setUserId } from "@services/StateManagement/UserSlice";
import { error_message } from "@constants/Errors";
import { validateUserExists } from "@services/API/Requests/UserRequests";
import useApiConnection from "../services/CheckConnection";

export const useAuthAppInitializer = () => {
  const { pathname } = useLocation();
  const dispatch = useDispatch();

  const adminsGroup = "admins";
  const realmString = "realm";

  const { keycloak, initialized } = useKeycloak();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>("");
  const [sessionExpired, setSessionExpired] = useState(false);
  const [connectionChecked, setConnectionChecked] = useState(false);
  const [isConnected, setIsConnected] = useState<boolean>(true);
  const [userChecked, setUserChecked] = useState(false);
  const [userData, setUserData] = useState<USER>();
  const [noUserFound, setNoUserFound] = useState(false);

  const adminRealm = import.meta.env.VITE_ADMIN_REALM;
  const adminHomePage = import.meta.env.VITE_APP_ADMIN_HOME_PAGE;
  const managementPage = import.meta.env.VITE_APP_MNG_HOME_PAGE;
  const isAdminApp = import.meta.env.VITE_ADMIN_APPLICATION === "true";

  const realm = localStorage.getItem(realmString);
  const isAdminLoggedIn = useSelector((state: RootState) => state.auth.isAdmin);

  useEffect(() => {
    if (isAdminApp && realm !== adminRealm) {
      localStorage.setItem(realmString, adminRealm);
      window.location.href = managementPage;
    } else if (!isAdminApp && realm === adminRealm) {
      localStorage.setItem(realmString, "");
      localStorage.setItem(LOGIN_STATES.PREVIOUS_STATE, LOGIN_STATES.LOGGED_IN);
      localStorage.setItem(
        LOGIN_STATES.CURRENT_STATE,
        LOGIN_STATES.LOGGING_OUT,
      );
      window.location.href = adminHomePage;
    }
  }, []);

  useEffect(() => {
    if (
      localStorage.getItem(LOGIN_STATES.CURRENT_STATE) === LOGIN_STATES.START
    ) {
      localStorage.setItem(
        LOGIN_STATES.PREVIOUS_STATE,
        LOGIN_STATES.LOGGING_IN,
      );
    }
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  useEffect(() => {
    setTimeout(() => setLoading(false), 1000);
  }, []);

  useEffect(() => {
    if (!keycloak || !keycloak.token) return;

    dispatch(setHeaders({ Authorization: `Bearer ${keycloak.token}` }));
    dispatch(setAuthState(true));
    dispatch(setCompanyIdState(keycloak.realm));
    dispatch(setUserId(keycloak.tokenParsed?.sub || ""));
    setUserChecked(true);

    const userGroups = keycloak.tokenParsed?.realm_access?.roles;
    const isAdmin = userGroups?.includes(adminsGroup);

    if (isAdmin && realm === adminRealm && !isAdminLoggedIn) {
      dispatch(setIsAdmin(true));
    } else {
      dispatch(setIsFirstUser(false));
    }
  }, [keycloak?.token]);

  useEffect(() => {
    const fetchUserData = async () => {
      if (!userChecked) return;
      setLoading(true);
      try {
        const response = await validateUserExists(
          setUserData,
          setLoading,
          setError,
          0,
          true,
        );
        setNoUserFound(!response.user_id);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchUserData();
  }, [userChecked]);

  // useMemo(() => {
  //   const checkConnection = async () => {
  //     if (connectionChecked) return;
  //     try {
  //       const connected = await useApiConnection();
  //       if (connected !== true) {
  //         setConnectionChecked(true);
  //         setIsConnected(false);
  //       }
  //     } catch {
  //       setIsConnected(false);
  //       setConnectionChecked(false);
  //     }
  //   };
  //   checkConnection();
  // }, [connectionChecked]);

  useEffect(() => {
    if (!initialized) return;

    const checkAuth = async () => {
      if (keycloak.authenticated) {
        localStorage.setItem(
          LOGIN_STATES.CURRENT_STATE,
          LOGIN_STATES.LOGGED_IN,
        );
      } else {
        const currentState = localStorage.getItem(LOGIN_STATES.CURRENT_STATE);
        if (currentState === LOGIN_STATES.LOGGED_IN) {
          localStorage.setItem(
            LOGIN_STATES.CURRENT_STATE,
            LOGIN_STATES.LOGGING_OUT,
          );
        } else {
          localStorage.setItem(
            LOGIN_STATES.CURRENT_STATE,
            LOGIN_STATES.LOGGING_IN,
          );
          await keycloak.login();
        }
      }
    };

    checkAuth();
  }, [initialized, keycloak]);

  useEffect(() => {
    const interval = setInterval(async () => {
      try {
        await keycloak.updateToken();
        dispatch(setHeaders({ Authorization: `Bearer ${keycloak.token}` }));
      } catch (err) {
        setSessionExpired(true);
        console.error(error_message.keycloak.token_refresh, err);
      }
    }, 50000);

    return () => clearInterval(interval);
  }, [keycloak]);

  useEffect(() => {
    if (sessionExpired) {
      localStorage.setItem(
        LOGIN_STATES.CURRENT_STATE,
        LOGIN_STATES.LOGGING_OUT,
      );
    }
  }, [sessionExpired]);

  return {
    loading,
    error,
    isAdminLoggedIn,
    sessionExpired,
    connectionChecked,
    isConnected,
    noUserFound,
    userData,
  };
};
